"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { AuthPageHeader } from "../page-header";
import { SubmitButton } from "@/components/ui/button";
import { OtpInput } from "../otp-input";
import { useMfaVerify } from "../../hooks/use-mfa-verify";
import {
  getMfaChallenge,
  clearMfaChallenge,
  type MfaChallengeData,
} from "../../lib/mfa-storage";
import { Icon } from "@/components/ui/icon";

export function MfaChallenge() {
  const router = useRouter();
  const mfaVerify = useMfaVerify();
  const [challengeData] = useState<MfaChallengeData | null>(() =>
    getMfaChallenge()
  );
  const [otpError, setOtpError] = useState(false);

  useEffect(() => {
    if (!challengeData) {
      router.replace("/login");
    }
  }, [challengeData, router]);

  function handleComplete(code: string) {
    if (!challengeData) return;
    setOtpError(false);
    mfaVerify.mutate(
      {
        code,
        pending_authentication_token: challengeData.pending_authentication_token,
        authentication_challenge_id: challengeData.authentication_challenge_id,
      },
      {
        onError: () => setOtpError(true),
      }
    );
  }

  if (!challengeData) return null;

  return (
    <>
      <AuthPageHeader
        title="2FA Verification"
        subtitle="Enter the current 6-digit verification code from your authenticator app that you setup in security settings."
      />

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between text-sm leading-5">
            <span className="font-medium text-foreground">Verification Code</span>
            <button
              type="button"
              onClick={() => {
                clearMfaChallenge();
                router.push("/login");
              }}
              className="text-foreground/70 cursor-pointer hover:text-foreground transition-colors"
            >
              Back to sign in
            </button>
          </div>

          <OtpInput
            length={6}
            onComplete={handleComplete}
            disabled={mfaVerify.isPending}
            error={otpError}
          />

          {otpError && (
            <p className="text-base text-[#ed6664] tracking-[-0.16px] leading-5">
              The code is invalid, please verify and try again.
            </p>
          )}
        </div>

        <SubmitButton type="button" variant="linear-ai" size="auth" loading={mfaVerify.isPending}>
          Confirm <Icon name="IconArrowRight" size={16} />
        </SubmitButton>
      </div>
    </>
  );
}
